import type { TeacherExpression, VisualSpec } from "../contracts";

export type GoldenSceneId = "G1" | "G2" | "G3" | "G4" | "G5" | "G6";

export type GoldenInteractionType =
  | "GREETING_PARTICIPATION"
  | "VOICE_IMITATION"
  | "CLAP_ACTION"
  | "SEMANTIC_NUMBER"
  | "SILENCE_SCAFFOLD"
  | "STORY_CELEBRATION";

export interface GoldenSceneSpec {
  readonly id: GoldenSceneId;
  readonly cardReferenceId: string;
  readonly title: string;
  readonly pedagogicalGoal: string;
  readonly teacherSpeech: string;
  readonly audioCueId: string;
  readonly interactionType: GoldenInteractionType;
  readonly expectedNumber?: 1 | 2 | 3;
  readonly expression: TeacherExpression;
  readonly visual: VisualSpec;
  readonly touchLabel: string;
  readonly hintText: string;
  readonly repromptText: string;
}

export const GOLDEN_SCENES: readonly GoldenSceneSpec[] = Object.freeze([
  {
    id: "G1",
    cardReferenceId: "D1-01",
    title: "지나쌤과 첫 인사",
    pedagogicalGoal: "살아있는 교사 탐색 및 교감 형성",
    teacherSpeech: "안녕? 난 지나쌤이야! 만나서 정말 반가워.",
    audioCueId: "D1-01:teacher",
    interactionType: "GREETING_PARTICIPATION",
    expression: "GREETING_WARM",
    visual: { type: "WELCOME", accent: "HELLO" },
    touchLabel: "안녕! 지나쌤 👋",
    hintText: "지나쌤에게 손을 흔들거나 목소리로 인사해 봐요.",
    repromptText: "안녕하고 인사해 줄래?",
  },
  {
    id: "G2",
    cardReferenceId: "D1-05",
    title: "소리 따라 외치기 (하나!)",
    pedagogicalGoal: "모방 발화 및 음성 참여 (VAD ≠ 정답 원칙)",
    teacherSpeech: "이제 쌤을 따라 해봐. 화면을 톡 누르면서 '하나!' 외쳐볼까?",
    audioCueId: "D1-05:teacher",
    interactionType: "VOICE_IMITATION",
    expectedNumber: 1,
    expression: "ENCOURAGING_SOFT",
    visual: { type: "NUMBER", value: 1 },
    touchLabel: "하나!",
    hintText: "숫자 1을 보며 '하나!' 하고 외쳐 봐요.",
    repromptText: "지나쌤처럼 '하나!' 해볼까?",
  },
  {
    id: "G3",
    cardReferenceId: "D1-13",
    title: "둘 하면 짝짝! 박수 놀이",
    pedagogicalGoal: "신체 리듬 활동 및 음향 박수(Transient) 감지",
    teacherSpeech: "둘! 하면 박수를 두 번! 짝짝!",
    audioCueId: "D1-13:teacher",
    interactionType: "CLAP_ACTION",
    expression: "ENCOURAGING_SOFT",
    visual: { type: "NUMBER", value: 2 },
    touchLabel: "짝짝! 👏",
    hintText: "손뼉을 짝짝! 두 번 쳐 봐요.",
    repromptText: "지나쌤이랑 같이 박수 쳐볼까? 짝! 짝!",
  },
  {
    id: "G4",
    cardReferenceId: "D1-23",
    title: "사과 세 개 맞추기 (의미적 판정)",
    pedagogicalGoal: "실제 유아 음성 의미론적 판정 (3 / 세 / 새 / 셋)",
    teacherSpeech: "우와, 사과가 더 많아졌네. 천천히 세어보고 숫자를 말해줘.",
    audioCueId: "D1-23:teacher",
    interactionType: "SEMANTIC_NUMBER",
    expectedNumber: 3,
    expression: "ASKING_CURIOUS",
    visual: { type: "APPLES", count: 3 },
    touchLabel: "사과 3개",
    hintText: "사과가 몇 개인지 말해 봐요. '셋!'",
    repromptText: "사과가 몇 개일까? 하나, 둘, 셋!",
  },
  {
    id: "G5",
    cardReferenceId: "D1-22",
    title: "기다림과 스캐폴딩 (사과 둘)",
    pedagogicalGoal: "침묵은 결함이 아님 — 기다림 후 발판(Scaffold) 제공",
    teacherSpeech: "이번엔 사과가 몇 개일까? 쌤이랑 같이 세어볼까? 하나, 둘!",
    audioCueId: "state:model-two-paced",
    interactionType: "SILENCE_SCAFFOLD",
    expectedNumber: 2,
    expression: "THINKING_CURIOUS",
    visual: { type: "APPLES", count: 2 },
    touchLabel: "사과 2개",
    hintText: "천천히 생각해도 괜찮아. 기다려줄게.",
    repromptText: "사과가 하나… 그리고 둘!",
  },
  {
    id: "G6",
    cardReferenceId: "D1-27",
    title: "오늘 내가 해냈어! 축하와 인사",
    pedagogicalGoal: "성취감 극대화 및 마이크로 세션 성공 완료",
    teacherSpeech: "오늘 처음부터 끝까지 혼자 힘으로 정말 열심히 해냈어! 쌤이 너무 뿌듯해.",
    audioCueId: "D1-27:teacher",
    interactionType: "STORY_CELEBRATION",
    expression: "CELEBRATING",
    visual: { type: "CELEBRATE", stars: 5 },
    touchLabel: "지나쌤 고마워요! ⭐",
    hintText: "오늘 열심히 한 나에게 큰 박수를 보내요!",
    repromptText: "다음에 또 만나요!",
  },
]);
